import React from 'react';

import { Dropdown } from '@column/column-ui-kit';

import { OptionsFull, OptionsNotCreatable, OptionsNotEditable, OptionsOnOff } from '../types';
import { RenderFields } from '~/components';
import { CustomRolePermissionLevel, CustomRolePlatformLevelPermissions } from '~/typings/API';

interface Props {
  excludeKeys?: (keyof CustomRolePlatformLevelPermissions)[];
  fieldPathPrefix?: string;
  includeKeys?: (keyof CustomRolePlatformLevelPermissions)[];
  onOffEnabledPermissionLevel?: CustomRolePermissionLevel;
}

export const AdditionalPermissions: React.FC<Props> = ({
  excludeKeys = [],
  fieldPathPrefix,
  includeKeys,
  onOffEnabledPermissionLevel = 'write',
}) => {
  const fieldPath = (field: string) => `${fieldPathPrefix ? `${fieldPathPrefix}.` : ''}${field}`;
  const isHidden = (key: keyof CustomRolePlatformLevelPermissions) =>
    excludeKeys.includes(key) || (includeKeys !== undefined && !includeKeys.includes(key));

  return RenderFields<any>({
    sections: [
      {
        hide: isHidden('platformInfo'),
        fields: [
          {
            id: fieldPath('platformLevelPermissions.platformInfo'),
            label: 'Platform Settings',
            description: 'View and edit platform information and due diligence.',
            children: ({ value, onChange }) => (
              <Dropdown
                active={value === 'default' ? 'none' : value}
                options={OptionsNotCreatable}
                onChange={onChange}
                fullWidth
              />
            ),
          },
        ],
      },
      {
        fields: [
          {
            hide: isHidden('counterparties'),
            id: fieldPath('platformLevelPermissions.counterparties'),
            label: 'Counterparties',
            description: 'Set the default permissions for new and existing counterparties.',
            children: ({ value, onChange }) => (
              <Dropdown
                active={value === 'default' ? 'none' : value}
                options={OptionsNotEditable}
                onChange={onChange}
                fullWidth
              />
            ),
          },
          {
            hide: isHidden('apiKeys'),
            id: fieldPath('platformLevelPermissions.apiKeys'),
            label: 'API Keys',
            description:
              'API keys allow developers on your team to connect to your platform. Access should be granted sparingly.',
            children: ({ value, onChange }) => (
              <Dropdown
                active={value === 'default' ? 'none' : value}
                options={OptionsFull}
                onChange={onChange}
                fullWidth
              />
            ),
          },
          {
            hide: isHidden('transferTemplates'),
            id: fieldPath('platformLevelPermissions.transferTemplates'),
            label: 'Transfer Templates',
            description: 'Set the default permissions for new and existing transfer templates.',
            children: ({ value, onChange }) => (
              <Dropdown
                active={value === 'default' ? 'none' : value}
                options={OptionsFull}
                onChange={onChange}
                fullWidth
              />
            ),
          },
          {
            hide: isHidden('webhooks'),
            id: fieldPath('platformLevelPermissions.webhooks'),
            label: 'Events and Webhooks',
            description: 'Set the default permissions for events and webhooks.',
            children: ({ value, onChange }) => (
              <Dropdown
                active={value === 'default' ? 'none' : value}
                options={OptionsFull}
                onChange={onChange}
                fullWidth
              />
            ),
          },
          {
            hide: isHidden('loans'),
            id: fieldPath('platformLevelPermissions.loans'),
            label: 'Loans',
            description: 'Set the default loan permissions for new and existing bank accounts.',
            children: ({ value, onChange }) => (
              <Dropdown
                active={value === 'default' ? 'none' : value}
                options={OptionsFull}
                onChange={onChange}
                fullWidth
              />
            ),
          },
        ],
      },
      {
        hide: isHidden('reporting'),
        fields: [
          {
            id: fieldPath('platformLevelPermissions.reporting'),
            label: 'Reports and Statements',
            description: 'Set the default reporting permissions for new and existing bank accounts.',
            children: ({ value, onChange }) => {
              const onOffValue = value === 'write' || value === 'read' ? 'on' : 'off';
              const onOffOnChange = (newValue: 'on' | 'off') =>
                newValue === 'on' ? onChange(onOffEnabledPermissionLevel) : onChange('none');
              return <Dropdown active={onOffValue} options={OptionsOnOff} onChange={onOffOnChange} fullWidth />;
            },
          },
        ],
      },
    ],
  });
};
